import { ReactElement, useState, useEffect, useCallback } from 'react';
import { notification, Modal, Row, Popover } from 'antd';
import styles from './DbUsers.module.less';
import AppEmpty from '../AppEmpty';
import { Link, useParams } from 'react-router-dom';
import useDbUsers from '../../core/hooks/useDbUsers';
import useDatabases from '../../core/hooks/useDatabases';
import AppTable from '../AppTable';
import { InfoCircleOutlined } from '@severalnines/frontend_hub/libs/icons';
import CopyToClipboardText from '../CopyToClipboardText';
import AddDbUser from './AddDbUser';
import AppConfirmDialog from '../AppConfirmDialog';
import DbUserService from '../../services/DbUserService';
import AppLoading from '../AppLoading';
import ConnectionAssistant from './ConnectionAssistant';
import DbUser, { DEFAULT_DATABASE_USER } from '../../types/DbUser';
import useDataStore from '../../core/hooks/useDataStore';
import CcxSectionHeader from '../ccx/common/CcxSectionHeader';
import CcxIconCheckCircle from '../ccx/icons/CcxIconCheckCircle';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import CcxIconInfoCircleTwoTone from '../ccx/icons/CcxIconInfoCircleTwoTone';
import CcxIconDeleteOutlined from '../ccx/icons/CcxIconDeleteOutlined';
import { Tooltip } from 'antd';
import TypographyText from '../../tmp/TypographyText';
import './DbUser.less';
import UpdateDbUserActionsMenu from './UpdateDbUserActionsMenu';
import useDataStoreJobs from '../../core/hooks/useDataStoreJobs';
import { isRebootingNode, isRestoringDatastore } from '../../core/helpers';
import useCmonStatus from '../../core/hooks/useCmonStatus';
import DeploymentsItem from '../../types/DeploymentsItem';
import { DeniedOutlined } from '@severalnines/frontend_hub/libs/icons';
import { CAN_DELETE_DATABASE_USER } from '../../core/PermissionRoles';
import Can from '../../core/providers/Can';
interface UrlProps {
    dataStoreUuid: string;
    projectUuid: string;
}

type DbUsersProps = {
    currentDeployment?: DeploymentsItem;
};

function DbUsers({ currentDeployment }: DbUsersProps): ReactElement {
    const { dataStoreUuid, projectUuid } = useParams<UrlProps>();
    const { dataStore: deployment } = useDataStore(dataStoreUuid);
    const {
        dbUsers,
        loading: dbUsersLoading,
        refresh: dbUsersRefresh,
    } = useDbUsers(dataStoreUuid); // @TODO: Add refresh here
    const { databases, loading: databasesLoading } =
        useDatabases(dataStoreUuid);
    const { jobs } = useDataStoreJobs({
        dataStoreUuid: deployment?.dataStoreUuid,
    });
    const [message, setMessage] = useState<string>(
        'No users have been added yet.'
    );
    const [dbUsersData, setDbUsersData] = useState<any>([]);
    const [hosts, setHosts] = useState<any>(undefined);
    const [dbService, setDbService] = useState('');
    const [usersHosts, setUsersHosts] = useState<string[]>([]);
    const { status } = useCmonStatus();
    const [defaultAdmin, setDefaultAdmin] = useState<string>('');

    useEffect(() => {
        if (deployment) {
            setHosts(deployment.getHosts());
        }
    }, [deployment]);

    const checkRestoringBackup = useCallback(() => {
        if (isRestoringDatastore(jobs)) {
            return setMessage('Cannot list users during backup restore.');
        }

        if (isRebootingNode(jobs)) {
            return setMessage('Cannot list users during node reboot.');
        }

        if (!status?.isFunctional() || !dbUsers) {
            return setMessage(
                'Cannot list users, the datastore is not available at the moment.'
            );
        }

        if (dbUsers.accounts.length === 0) {
            return setMessage('No users have been added yet.');
        }
    }, [status, dbUsers, jobs]);

    useEffect(() => {
        checkRestoringBackup();
    }, [jobs, checkRestoringBackup, status]);

    const tableFooter = () => (
        <>
            <InfoCircleOutlined /> Accounts are allowed to connect from trusted
            sources controlled by the{' '}
            <Link
                to={`/projects/${projectUuid}/data-stores/${dataStoreUuid}/firewall`}
            >
                Firewall
            </Link>
        </>
    );

    useEffect(() => {
        setDbService(currentDeployment?.getServiceName() || '');

        if (dbUsers && dbUsers.accounts) {
            const data = dbUsers.accounts.map((u: DbUser) => {
                if (u.isDefaultUser()) {
                    setDefaultAdmin(u.username);
                }
                return {
                    ...u,
                    uniqueGrants: u.getUniqueGrants(),
                };
            });
            const sortedUsers = [...data].sort((a, b) =>
                a.username.localeCompare(b.username)
            );

            setDbUsersData(sortedUsers);
        }
    }, [dbUsers, currentDeployment]);

    useEffect(() => {
        if (dbUsers && dbUsers.accounts) {
            setUsersHosts(
                dbUsers.accounts?.map((u: DbUser) => {
                    return u.username;
                })
            );
        }
    }, [dbUsers]);

    const usersColumns: (
        | {
              width: number;
              title: JSX.Element | string;
              render: (text: string, record: any) => JSX.Element;
              key: string;
              fixed?: string;
          }
        | {
              title: string;
              render: (text: string, record: any) => JSX.Element;
              key: string;
              ellipsis: boolean;
          }
    )[] = [
        {
            title: 'Account',
            key: 'user',
            width: 100,
            render: (text: string, record: any) => {
                return currentDeployment?.isMariaDb() ||
                    currentDeployment?.isPercona() ? (
                    <CopyToClipboardText text={record.username} />
                ) : (
                    <CopyToClipboardText text={`${record.username}`} />
                );
            },
        },
        {
            title: currentDeployment?.isRedis() ? 'ACL' : 'Privileges',
            key: currentDeployment?.isRedis() ? 'ACL' : 'privileges',
            width: 400,
            ellipsis: true,
            render: (text: string, record: any) => {
                const separator = currentDeployment?.isRedis() ? ' ' : ';';
                const privileges = record?.grants
                    ?.split(separator)
                    ?.map((privilege: string) => {
                        if (privilege.indexOf(':') === -1) {
                            return {
                                db: '*.*',
                                privileges: privilege.split(',').sort(),
                            };
                        }
                        const [database, privileges] = privilege.split(':');

                        return {
                            db: database,
                            privileges: privileges.split(',').sort(),
                        };
                    });

                return (
                    <Row
                        gutter={[16, 16]}
                        className={styles.PrivilegesContainer}
                    >
                        {privileges?.map((priv: any) => {
                            return (
                                <div className={styles.DbUsersSegmentedData}>
                                    <div
                                        className={
                                            styles.DbUsersSegmentedFirstItem
                                        }
                                    >
                                        {priv.db}
                                    </div>
                                    <Popover
                                        content={
                                            <Row>
                                                {priv.privileges.filter(
                                                    (priv: string) =>
                                                        priv !== ''
                                                ).length
                                                    ? priv.privileges?.map(
                                                          (item: string) => (
                                                              <div
                                                                  className={
                                                                      styles.DbUserPopoverCol
                                                                  }
                                                              >
                                                                  <ul>
                                                                      <li
                                                                          key={
                                                                              item
                                                                          }
                                                                      >
                                                                          {item.trim()}
                                                                      </li>
                                                                  </ul>
                                                              </div>
                                                          )
                                                      )
                                                    : currentDeployment?.isMSSQL() &&
                                                      `Sysadmin`}
                                            </Row>
                                        }
                                        title={
                                            <span
                                                className={
                                                    styles.DBUserPopoverTitle
                                                }
                                            >
                                                {priv.db === '*.*'
                                                    ? 'Global Privileges'
                                                    : 'Privileges'}
                                            </span>
                                        }
                                        destroyTooltipOnHide={true}
                                        arrowPointAtCenter={false}
                                        overlayInnerStyle={{
                                            padding: 0,
                                            borderRadius: 16,
                                        }}
                                        overlayStyle={{
                                            maxWidth: 400,
                                            minWidth: 320,
                                        }}
                                        overlayClassName="DbUserPopover"
                                    >
                                        <div
                                            className={
                                                styles.DbUsersSegmentedSecondItem
                                            }
                                        >
                                            {priv.privileges.filter(
                                                (priv: string) => priv !== ''
                                            ).length
                                                ? priv.privileges.join(', ')
                                                : currentDeployment?.isMSSQL() &&
                                                  `Sysadmin`}
                                        </div>
                                    </Popover>
                                </div>
                            );
                        })}
                    </Row>
                );
            },
        },
    ];
    if (currentDeployment?.isPercona()) {
        usersColumns.push({
            title: 'Authentication Plugin',
            key: 'authPlugin',
            ellipsis: true,
            width: 100,
            render: (text: string, record: any) => {
                return record.authPlugin;
            },
        });
    }

    usersColumns.push({
        title: <TypographyText nowrap={true}>Actions</TypographyText>,
        key: 'actions',
        width: 90,
        render: (text: string, record: any) => {
            const onConfirmDelete = () => {
                return new Promise(async (resolve, reject) => {
                    notification.open({
                        message: 'Remove account',
                        description: 'The account will be removed soon.',
                        icon: <CcxIconInfoCircleTwoTone />,
                    });

                    Modal.destroyAll();

                    try {
                        const data = {
                            uuid: dataStoreUuid,
                            dbUsername: record.username,
                            dbHost: record.hostAllow,
                        };
                        await DbUserService.deleteUser(data);

                        dbUsersRefresh && (await dbUsersRefresh());
                        // dispatch(getCurrentDataStore({ dataStoreUuid }));

                        notification.open({
                            message: 'Remove Account',
                            description: 'Account successfully removed',
                            icon: <CcxIconCheckCircle />,
                        });

                        resolve(null);
                    } catch (e) {
                        notification.open({
                            message: 'Remove Account',
                            description: `There was an error removing the account. ${e}`,
                            icon: (
                                <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />
                            ),
                        });

                        console.error(e);

                        reject();
                    }
                }).catch(() => console.log('Oops errors!'));
            };

            return (
                <div className={styles.DbUsersOptionsColumn}>
                    {record.username === DEFAULT_DATABASE_USER ? (
                        <Tooltip
                            placement="top"
                            title="Default user cannot be deleted or changed"
                        >
                            <DeniedOutlined className={styles.DbUsersStop} />
                        </Tooltip>
                    ) : currentDeployment?.isRedis() ? (
                        <UpdateDbUserActionsMenu
                            dbUsersRefresh={dbUsersRefresh}
                            uuid={dataStoreUuid}
                            user={record}
                            databases={databases?.databases}
                            deployment={currentDeployment}
                            onConfirmDelete={onConfirmDelete}
                        />
                    ) : (
                        <Can
                            permission={CAN_DELETE_DATABASE_USER}
                            showTooltip={true}
                        >
                            <AppConfirmDialog
                                critical={true}
                                onOk={onConfirmDelete}
                                content="The account will be removed and will not be able to connect to the database."
                                actionIcon={<CcxIconDeleteOutlined />}
                            />
                        </Can>
                    )}
                </div>
            );
        },
    });

    return (
        <section className={styles.DbUsers}>
            <CcxSectionHeader>
                <div className={styles.ConnectionHeaderButtons}>
                    {!currentDeployment?.isValkey() &&
                        !currentDeployment?.isRedis() && (
                            <ConnectionAssistant
                                databases={databases?.databases}
                                users={usersHosts}
                                hosts={hosts}
                                service={dbService}
                                datastore={currentDeployment as any}
                                disabled={
                                    dbUsersLoading ||
                                    databasesLoading ||
                                    isRestoringDatastore(jobs) ||
                                    !currentDeployment?.isOperable()
                                }
                            />
                        )}
                    {!databasesLoading && (
                        <AddDbUser
                            onSuccess={dbUsersRefresh}
                            uuid={dataStoreUuid}
                            databases={databases?.databases}
                            currentDeployment={currentDeployment}
                            defaultAdmin={defaultAdmin}
                            disabled={
                                dbUsersLoading ||
                                databasesLoading ||
                                isRestoringDatastore(jobs) ||
                                !currentDeployment?.isOperable()
                            }
                        />
                    )}
                </div>
            </CcxSectionHeader>
            <h4 className={styles.DbUsersLabel}>Users</h4>
            {dbUsersLoading ? (
                <AppLoading />
            ) : dbUsers?.accounts?.length ? (
                <AppTable
                    columns={usersColumns}
                    data={dbUsersData}
                    rowKey="uniqueKey"
                    expandable={false}
                    footer={
                        currentDeployment && !currentDeployment.isVMWare()
                            ? tableFooter
                            : undefined
                    }
                    pagination={{
                        hideOnSinglePage: true,
                    }}
                />
            ) : (
                <AppEmpty message={message} />
            )}
        </section>
    );
}

export default DbUsers;
